<template>
  <modal
    :loading="loading"
    :title="$t('set_label_sets.create_from_csv_title')"
    style="max-width: 780px"
    @close_modal="close_modal"
  >
    <template v-slot:content>
      <v-text-field
        id="csv_labelset_name"
        v-model="label_set_name"
        :label="$t('set_label_sets.edit_name')"
        required
      ></v-text-field>
      <v-text-field
        id="csv_labelset_description"
        v-model="label_set_description"
        :label="$t('set_label_sets.edit_description')"
      ></v-text-field>

      <p class="import-help">{{ $t('set_label_sets.create_from_csv_help') }}</p>
      <p class="import-format">
        <code>name</code>
        {{ $t('set_labels.import_format_or') }}
        <code>name,color,type,parent</code>
      </p>
      <p class="import-defaults">{{ $t('set_labels.import_defaults') }}</p>

      <v-file-input
        id="create_labelset_csv_file"
        v-model="file"
        accept=".csv,text/csv"
        :label="$t('set_label_sets.create_from_csv_file_label')"
        prepend-icon="mdi-file-delimited"
        show-size
      ></v-file-input>

      <div v-if="parseError" id="create_labelset_csv_error" class="import-error">
        {{ parseError }}
      </div>

      <div
        v-if="csvLabels.length > 0"
        id="create_labelset_csv_summary"
        class="import-summary"
      >
        <div>
          {{ $t('set_label_sets.csv_labels_in_file', { n: csvLabels.length }) }}
        </div>
        <div id="create_labelset_csv_existing">
          {{ $t('set_labels.import_existing_count', { n: existing.length }) }}
        </div>
        <div id="create_labelset_csv_missing">
          {{ $t('set_labels.import_missing_count', { n: missing.length }) }}
        </div>
      </div>

      <div
        v-if="missing.length > 0"
        id="create_labelset_csv_missing_list"
        class="missing-block"
      >
        <h4>{{ $t('set_labels.import_missing_title') }}</h4>
        <v-chip
          v-for="row in displayedMissing"
          :key="row.name"
          class="ma-1"
          small
          :style="missingChipStyle(row)"
        >
          {{ row.name }}
        </v-chip>
        <v-chip v-if="hiddenMissingCount > 0" small class="ma-1">
          {{ $t('set_label_sets.selected_labels_more', { n: hiddenMissingCount }) }}
        </v-chip>

        <v-checkbox
          id="create_labelset_csv_add_missing"
          v-model="addMissing"
          :label="$t('set_labels.import_add_missing')"
          density="compact"
          hide-details
          class="mt-3"
        ></v-checkbox>
      </div>

      <v-alert
        v-if="csvLabels.length > 0 && missing.length === 0"
        type="success"
        variant="tonal"
        class="mt-3"
        id="create_labelset_csv_all_exist"
      >
        {{ $t('set_labels.import_all_exist') }}
      </v-alert>

      <v-alert
        v-if="missing.length > 0 && !addMissing"
        type="warning"
        variant="tonal"
        class="mt-3"
        id="create_labelset_csv_skip_missing_warn"
      >
        {{ $t('set_label_sets.create_from_csv_skip_missing') }}
      </v-alert>
    </template>

    <template v-slot:buttons>
      <v-btn id="create_labelset_csv_cancel" color="primary" text @click="close_modal">
        {{ $t('set_label_sets.btn_cancel') }}
      </v-btn>
      <v-btn
        id="create_labelset_csv_confirm"
        color="primary"
        text
        :disabled="!canCreate"
        @click="createLabelSet"
      >
        {{ $t('set_label_sets.btn_create_labelset') }}
      </v-btn>
    </template>
  </modal>
</template>

<script>
import Modal from '@/components/Modal';
import {
  parseLabelCsv,
  partitionLabelsViaApi,
  createMissingLabels,
  applyImportDefaults,
} from '@/utils/labelCsv';

const PREVIEW_LIMIT = 20;

export default {
  name: 'CreateLabelSetFromCsvModal',
  components: { Modal },
  data() {
    return {
      loading: false,
      file: null,
      label_set_name: '',
      label_set_description: '',
      csvLabels: [],
      existing: [],
      missing: [],
      addMissing: true,
      parseError: null,
    };
  },
  computed: {
    displayedMissing() {
      return this.missing.slice(0, PREVIEW_LIMIT);
    },
    hiddenMissingCount() {
      return Math.max(0, this.missing.length - PREVIEW_LIMIT);
    },
    canCreate() {
      if (!this.label_set_name || !this.label_set_name.trim()) {
        return false;
      }
      if (this.csvLabels.length === 0) {
        return false;
      }
      if (this.missing.length > 0 && !this.addMissing && this.existing.length === 0) {
        return false;
      }
      return true;
    },
  },
  watch: {
    file() {
      this.onFileChange();
    },
  },
  methods: {
    close_modal() {
      this.$emit('close_modal');
    },
    missingChipStyle(row) {
      if (!row || !row.color) {
        return {};
      }
      return {
        backgroundColor: `rgb(${row.color})`,
        color: '#fff',
      };
    },
    async onFileChange() {
      this.parseError = null;
      this.csvLabels = [];
      this.existing = [];
      this.missing = [];
      const f = Array.isArray(this.file) ? this.file[0] : this.file;
      if (!f) {
        return;
      }
      this.loading = true;
      try {
        const text = await f.text();
        const parsed = parseLabelCsv(text);
        if (!parsed.length) {
          this.parseError = this.$t('set_labels.import_empty');
          return;
        }
        const parts = await partitionLabelsViaApi(this.$api.label, parsed);
        this.existing = parts.existing;
        const existingForColors = parts.existing.map((e) => e.box21_label || e);
        this.missing = applyImportDefaults(parts.missing, existingForColors);
        this.csvLabels = [...this.existing, ...this.missing];
        this.addMissing = this.missing.length > 0;
      } catch (e) {
        console.error(e);
        this.parseError = this.$t('set_labels.import_parse_error');
      } finally {
        this.loading = false;
      }
    },
    async createLabelSet() {
      if (!this.canCreate) {
        return;
      }
      this.loading = true;
      try {
        let nameToId = new Map();
        for (const e of this.existing) {
          const label = e.box21_label || e;
          if (label && label.name && label.id) {
            nameToId.set(String(label.name).toLowerCase(), label.id);
          }
        }

        if (this.missing.length > 0 && this.addMissing) {
          const existingForParents = this.existing.map((e) => e.box21_label || e);
          const result = await createMissingLabels(
            this.$api.label,
            this.missing,
            existingForParents,
          );
          nameToId = result.nameToId;
          this.$store.commit('label/SET_LOADED', false);
          this.$store.commit('label/SET_LOADED_FOR_PROJECT_ID', null);
          this.$store.commit('label/set_items', []);
          await this.$store.dispatch('label/refresh_total_count');
        }

        const labelIds = [];
        const seen = new Set();
        for (const row of this.csvLabels) {
          const id = nameToId.get(String(row.name).toLowerCase());
          if (id != null && !seen.has(id)) {
            seen.add(id);
            labelIds.push(id);
          }
        }

        if (labelIds.length === 0) {
          await this.$store.dispatch('snackbars/add_failed_snack', {
            message: this.$t('set_label_sets.create_from_csv_no_labels'),
          });
          return;
        }

        const labelSet = await this.$api.label_set.addLabelSet(
          this.label_set_name.trim(),
          this.label_set_description || '',
        );
        await this.$api.label_set.updateLabels(labelSet, labelIds);

        await this.$store.dispatch('snackbars/add_success_snack', {
          message: this.$t('set_label_sets.create_from_csv_success', {
            n: labelIds.length,
          }),
        });
        this.$emit('reload');
        this.$emit('close_modal');
      } catch (e) {
        console.error(e);
        await this.$store.dispatch('snackbars/add_failed_snack', {
          message: this.$t('set_label_sets.create_from_csv_failed'),
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.import-help {
  margin-bottom: 4px;
}
.import-format {
  margin-bottom: 4px;
  color: #555;
}
.import-defaults {
  margin-bottom: 16px;
  color: #666;
  font-size: 0.9em;
}
.import-error {
  color: #b71c1c;
  margin-bottom: 12px;
}
.import-summary {
  margin: 12px 0;
  font-weight: 500;
}
.missing-block {
  margin-top: 12px;
  padding: 12px;
  background: #fff8e1;
  border-radius: 4px;
}
</style>
